import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../../lib/supabase';

// Detect if content is HTML (vs markdown)
const isHtmlContent = (content: string): boolean => {
  // Check for common HTML tags that would indicate HTML content
  const htmlPattern = /<(html|body|div|p|span|table|ul|ol|h[1-6]|br|strong|em|a)\b[^>]*>/i;
  return htmlPattern.test(content);
};

// Sanitize HTML to prevent XSS attacks
const sanitizeHtml = (html: string): string => {
  // Remove script tags and their content
  let clean = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  // Remove onclick, onerror, onload, etc. event handlers
  clean = clean.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
  clean = clean.replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '');

  // Remove javascript: URLs
  clean = clean.replace(/href\s*=\s*["']javascript:[^"']*["']/gi, 'href="#"');
  clean = clean.replace(/src\s*=\s*["']javascript:[^"']*["']/gi, 'src=""');

  // Remove data: URLs (can be used for XSS)
  clean = clean.replace(/src\s*=\s*["']data:[^"']*["']/gi, 'src=""');

  // Remove style tags (could contain expressions in older IE)
  clean = clean.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');

  // Remove iframe, object, embed tags
  clean = clean.replace(/<(iframe|object|embed|form|input|button)\b[^>]*>.*?<\/\1>/gi, '');
  clean = clean.replace(/<(iframe|object|embed|form|input|button)\b[^>]*\/?>/gi, '');

  return clean;
};

const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      try {
        const supabase = getSupabaseClient();

        // First try to get seeker-specific privacy policy
        let { data, error: fetchError } = await supabase
          .from('privacy_policy')
          .select('content')
          .eq('audience', 'seeker')
          .single();

        // If no seeker-specific policy, fall back to shared (null audience)
        if (fetchError || !data) {
          const result = await supabase
            .from('privacy_policy')
            .select('content')
            .is('audience', null)
            .single();

          data = result.data;
          fetchError = result.error;
        }

        if (fetchError) {
          console.error('Error fetching privacy policy:', fetchError);
          setError('Failed to load privacy policy. Please refresh the page.');
          return;
        }

        if (data) {
          setContent(data.content);
        }
      } catch (err) {
        console.error('Unexpected error fetching privacy policy:', err);
        setError('An unexpected error occurred. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrivacyPolicy();
  }, []);

  const handleGoBack = () => {
    navigate(-1);
  };

  // Enhanced markdown renderer with tables, blockquotes, links, etc.
  const renderMarkdown = (markdown: string) => {
    const lines = markdown.split('\n');
    const elements: React.ReactNode[] = [];
    let currentList: string[] = [];
    let listType: 'ul' | 'ol' | null = null;
    let currentBlockquote: string[] = [];
    let currentTable: string[][] = [];
    let tableAlignment: ('left' | 'center' | 'right')[] = [];

    const flushList = () => {
      if (currentList.length > 0) {
        const ListTag = listType === 'ol' ? 'ol' : 'ul';
        elements.push(
          <ListTag key={elements.length} className={listType === 'ol' ? 'list-decimal list-inside space-y-1 mb-4' : 'list-disc list-inside space-y-1 mb-4'}>
            {currentList.map((item, i) => (
              <li key={i} className="text-[16px] leading-7 text-[#2C2731]">{renderInlineMarkdown(item)}</li>
            ))}
          </ListTag>
        );
        currentList = [];
        listType = null;
      }
    };

    const flushBlockquote = () => {
      if (currentBlockquote.length > 0) {
        elements.push(
          <blockquote key={elements.length} className="border-l-4 border-[#214D3C] pl-4 py-2 my-4 bg-[#214D3C]/5 rounded-r">
            {currentBlockquote.map((line, i) => (
              <p key={i} className="text-[16px] leading-7 text-[#2C2731] italic">{renderInlineMarkdown(line)}</p>
            ))}
          </blockquote>
        );
        currentBlockquote = [];
      }
    };

    const flushTable = () => {
      if (currentTable.length > 0) {
        const headerRow = currentTable[0];
        const bodyRows = currentTable.slice(1);
        elements.push(
          <div key={elements.length} className="overflow-x-auto my-4">
            <table className="min-w-full border-collapse border border-[#304031]/30 text-[16px]">
              <thead>
                <tr className="bg-[#214D3C]/10">
                  {headerRow.map((cell, i) => (
                    <th
                      key={i}
                      className={`border border-[#304031]/30 px-4 py-2 font-semibold text-[#304031] ${
                        tableAlignment[i] === 'center' ? 'text-center' :
                        tableAlignment[i] === 'right' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {renderInlineMarkdown(cell.trim())}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row, rowIndex) => (
                  <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-[#FFFCEF]/50'}>
                    {row.map((cell, cellIndex) => (
                      <td
                        key={cellIndex}
                        className={`border border-[#304031]/30 px-4 py-2 text-[#2C2731] ${
                          tableAlignment[cellIndex] === 'center' ? 'text-center' :
                          tableAlignment[cellIndex] === 'right' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {renderInlineMarkdown(cell.trim())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        currentTable = [];
        tableAlignment = [];
      }
    };

    const renderInlineMarkdown = (text: string): React.ReactNode => {
      const result: React.ReactNode[] = [];
      let remaining = text;
      let keyCounter = 0;

      while (remaining.length > 0) {
        // Check for links [text](url)
        const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
        if (linkMatch) {
          result.push(
            <a key={keyCounter++} href={linkMatch[2]} className="text-[#214D3C] underline hover:text-[#304031]" target="_blank" rel="noopener noreferrer">
              {linkMatch[1]}
            </a>
          );
          remaining = remaining.slice(linkMatch[0].length);
          continue;
        }

        // Check for inline code `code`
        const codeMatch = remaining.match(/^`([^`]+)`/);
        if (codeMatch) {
          result.push(
            <code key={keyCounter++} className="bg-[#2C2731]/10 px-1.5 py-0.5 rounded text-[15px] font-mono">
              {codeMatch[1]}
            </code>
          );
          remaining = remaining.slice(codeMatch[0].length);
          continue;
        }

        // Check for strikethrough ~~text~~
        const strikeMatch = remaining.match(/^~~([^~]+)~~/);
        if (strikeMatch) {
          result.push(<del key={keyCounter++}>{strikeMatch[1]}</del>);
          remaining = remaining.slice(strikeMatch[0].length);
          continue;
        }

        // Check for bold **text**
        const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
        if (boldMatch) {
          result.push(<strong key={keyCounter++}>{boldMatch[1]}</strong>);
          remaining = remaining.slice(boldMatch[0].length);
          continue;
        }

        // Check for italic *text*
        const italicMatch = remaining.match(/^\*([^*]+)\*/);
        if (italicMatch) {
          result.push(<em key={keyCounter++}>{italicMatch[1]}</em>);
          remaining = remaining.slice(italicMatch[0].length);
          continue;
        }

        // No match, take next character
        const nextSpecial = remaining.search(/[\[`~*]/);
        if (nextSpecial === -1) {
          result.push(remaining);
          break;
        } else if (nextSpecial === 0) {
          result.push(remaining[0]);
          remaining = remaining.slice(1);
        } else {
          result.push(remaining.slice(0, nextSpecial));
          remaining = remaining.slice(nextSpecial);
        }
      }

      return result.length === 1 ? result[0] : result;
    };

    const parseTableRow = (line: string): string[] => {
      return line.replace(/^\||\|$/g, '').split('|');
    };

    const isTableSeparator = (line: string): boolean => {
      return /^\|?[\s\-:|]+\|?$/.test(line) && line.includes('-');
    };

    const parseTableAlignment = (line: string): ('left' | 'center' | 'right')[] => {
      return parseTableRow(line).map(cell => {
        const trimmed = cell.trim();
        if (trimmed.startsWith(':') && trimmed.endsWith(':')) return 'center';
        if (trimmed.endsWith(':')) return 'right';
        return 'left';
      });
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      // Check for table rows
      if (trimmedLine.startsWith('|') || (trimmedLine.includes('|') && currentTable.length > 0)) {
        flushList();
        flushBlockquote();

        if (isTableSeparator(trimmedLine)) {
          tableAlignment = parseTableAlignment(trimmedLine);
        } else {
          currentTable.push(parseTableRow(trimmedLine));
        }
        return;
      } else if (currentTable.length > 0) {
        flushTable();
      }

      // Check for horizontal rule
      if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmedLine)) {
        flushList();
        flushBlockquote();
        elements.push(<hr key={index} className="my-6 border-t border-[#304031]/30" />);
        return;
      }

      // Check for blockquote
      if (trimmedLine.startsWith('> ')) {
        flushList();
        currentBlockquote.push(trimmedLine.slice(2));
        return;
      } else if (currentBlockquote.length > 0) {
        flushBlockquote();
      }

      // Check for headings
      if (trimmedLine.startsWith('### ')) {
        flushList();
        elements.push(
          <h3 key={index} className="text-lg font-semibold text-[#304031] mt-6 mb-3">
            {renderInlineMarkdown(trimmedLine.slice(4))}
          </h3>
        );
      } else if (trimmedLine.startsWith('## ')) {
        flushList();
        elements.push(
          <h2 key={index} className="text-xl font-semibold text-[#304031] mt-8 mb-4">
            {renderInlineMarkdown(trimmedLine.slice(3))}
          </h2>
        );
      } else if (trimmedLine.startsWith('# ')) {
        flushList();
        elements.push(
          <h1 key={index} className="font-recoleta text-2xl font-normal text-[#304031] mt-6 mb-4">
            {renderInlineMarkdown(trimmedLine.slice(2))}
          </h1>
        );
      } else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
        if (listType !== 'ul') {
          flushList();
          listType = 'ul';
        }
        currentList.push(trimmedLine.slice(2));
      } else if (/^\d+\.\s/.test(trimmedLine)) {
        if (listType !== 'ol') {
          flushList();
          listType = 'ol';
        }
        currentList.push(trimmedLine.replace(/^\d+\.\s/, ''));
      } else if (trimmedLine === '') {
        flushList();
      } else {
        flushList();
        elements.push(
          <p key={index} className="text-[16px] leading-7 text-[#2C2731] mb-4">
            {renderInlineMarkdown(trimmedLine)}
          </p>
        );
      }
    });

    flushList();
    flushBlockquote();
    flushTable();
    return elements;
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#FFFCEF]">
        <div className="text-center">
          <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#2C2731] border-r-transparent" />
          <p className="text-sm text-[#2C2731]/70">Loading privacy policy...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#FFFCEF]">
        <div className="max-w-md rounded-2xl border border-red-300 bg-red-50 p-6 text-center">
          <p className="mb-2 text-sm font-semibold text-red-900">Error Loading Privacy Policy</p>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full items-stretch overflow-hidden">
      <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
        <header className="flex h-14 flex-shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 text-sm text-[#FFFCEF] hover:text-[#FFFCEF]/80 transition-colors"
          >
            <span className="text-lg leading-none">&larr;</span>
            <span>Back</span>
          </button>
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-7 w-auto" />
          </div>
          <div className="w-20" aria-hidden />
        </header>

        <main className="flex flex-1 min-h-0 flex-col overflow-hidden px-6 pb-6 pt-6">
          <div className="mx-auto w-full max-w-4xl flex flex-col flex-1 min-h-0">
            <div className="flex flex-col gap-2 mb-6 flex-shrink-0">
              <h1 className="font-recoleta text-[32px] font-normal leading-[1.1] text-[#304031]">Privacy Policy</h1>
              <p className="text-[14px] leading-relaxed text-[#214D3C]">
                How we collect, use, and protect your information.
              </p>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto rounded-3xl border border-[#304031]/15 bg-white/70 p-6 shadow-inner">
              <div className="prose prose-sm max-w-none [&_a]:text-[#214D3C] [&_a]:underline [&_a]:cursor-pointer hover:[&_a]:text-[#304031]">
                {isHtmlContent(content) ? (
                  <div
                    className="html-content"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
                  />
                ) : (
                  renderMarkdown(content)
                )}
              </div>
            </div>

            <div className="mt-4 flex justify-center flex-shrink-0">
              <button
                onClick={handleGoBack}
                className="inline-flex items-center gap-2 rounded-full border border-[#304031] bg-[#304031] px-6 py-3 text-sm font-medium text-[#FFFCEF] shadow-lg transition-all hover:bg-[#214D3C] hover:-translate-y-0.5"
              >
                <span className="text-lg leading-none">&larr;</span>
                <span>Back</span>
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
